import React, { useState, useMemo, useCallback } from "react";
import {
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  ResponsiveContainer,
} from "recharts";
import { Form } from "react-bootstrap";

// Memoized chart component to prevent unnecessary re-renders
const GroupsChart = React.memo(({ data, isModal }) => {
  return (
    <ResponsiveContainer
      style={{ fontSize: isModal ? 18 : 11 }}
      height={isModal ? 350 : 150}
      width="100%"
    >
      <BarChart
        data={data}
        margin={{ top: 20, right: 20, left: 20, bottom: 20 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.1)" />
        <XAxis dataKey="name" stroke="#636e72" />
        <YAxis tickMargin={20} stroke="#636e72" />
        <Tooltip
          contentStyle={{
            backgroundColor: "#fff",
            border: "none",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            fontSize: isModal ? "18px" : "12px",
          }}
        />
        <Bar
          dataKey="חניכים"
          fill="#0066CC"
          radius={[4, 4, 0, 0]}
          barSize={isModal ? 40 : 25}
        />
      </BarChart>
    </ResponsiveContainer>
  );
});

// Memoized DojoSelector component
const DojoSelector = React.memo(({ selectedDojo, dojos, onDojoChange }) => (
  <div style={{ marginTop: "20px" }}>
    <Form.Select
      value={selectedDojo}
      onChange={onDojoChange}
      style={{
        maxWidth: "300px",
        width: "auto",
        margin: "0",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        padding: "8px 12px",
        paddingLeft: "40px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        textAlign: "right",
        direction: "rtl",
        appearance: "none",
        backgroundImage:
          "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3e%3c/svg%3e\")",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "left 0.75rem center",
        backgroundSize: "16px 12px",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {dojos.map((dojo) => (
        <option key={dojo} value={dojo}>
          {dojo === "all" ? "כל הסניפים" : `סניף ${dojo}`}
        </option>
      ))}
    </Form.Select>
  </div>
));

function NumberOfStudentsInEachGroupPane({
  groups,
  students,
  isModal = false,
}) {
  const [selectedDojo, setSelectedDojo] = useState("all");

  // Get unique dojos from groups
  const dojos = useMemo(
    () => ["all", ...new Set(groups.map((group) => group.dojo))],
    [groups]
  );

  // Memoize the filtered groups data
  const groupsStudentsData = useMemo(() => {
    const filteredGroups =
      selectedDojo === "all"
        ? groups
        : groups.filter((group) => group.dojo === selectedDojo);

    return filteredGroups.map((group) => {
      const studentCount = students.filter((student) =>
        student?.groupsIDs?.includes(group.id)
      ).length;
      return { name: group.name, חניכים: studentCount };
    });
  }, [groups, students, selectedDojo]);

  // Memoize the dojo change handler
  const handleDojoChange = useCallback((e) => {
    setSelectedDojo(e.target.value);
  }, []);

  if (!isModal) {
    return (
      <div
        style={{
          direction: "rtl",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <div style={{ marginTop: "auto" }}>
          <DojoSelector
            selectedDojo={selectedDojo}
            dojos={dojos}
            onDojoChange={handleDojoChange}
          />
        </div>
        <GroupsChart data={groupsStudentsData} isModal={isModal} />
      </div>
    );
  } else {
    return (
      <div
        style={{
          direction: "rtl",
          height: isModal ? 600 : 200,
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <DojoSelector
          selectedDojo={selectedDojo}
          dojos={dojos}
          onDojoChange={handleDojoChange}
        />
        <GroupsChart data={groupsStudentsData} isModal={isModal} />
      </div>
    );
  }
}

export default React.memo(NumberOfStudentsInEachGroupPane);
